import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import axios from 'axios';
import {
    FaHeadset,
    FaPlus,
    FaTicketAlt,
    FaInfoCircle,
    FaCheckCircle,
    FaClock,
    FaEnvelopeOpenText,
    FaExclamationTriangle,
    FaSearch,
    FaShieldAlt,
    FaBolt,
    FaCommentDots,
    FaTimes,
    FaPaperPlane,
    FaRegCommentDots,
    FaQuestionCircle,
    FaChevronDown,
    FaChevronUp
} from 'react-icons/fa';
import DataTable from '../../SuperAdmin/shared/DataTable';
import { showSuccess, showError, showLoading, closeLoading } from '../../../utils/Alert';
import Loader from '../../shared/Loader';
import '../../Student/views/StudentSupport.css';

const STATUS_CONFIG = {
    'Open': { icon: <FaClock />, className: 'open' },
    'In Progress': { icon: <FaBolt />, className: 'in-progress' },
    'Resolved': { icon: <FaCheckCircle />, className: 'resolved' },
    'Closed': { icon: <FaTimes />, className: 'closed' },
};

const JobSeekerSupport = () => {
    const [tickets, setTickets] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [showForm, setShowForm] = useState(false);
    const [form, setForm] = useState({ subject: '', category: 'General Query', description: '', priority: 'Normal' });
    const [submitting, setSubmitting] = useState(false);
    const [isThreadModalOpen, setIsThreadModalOpen] = useState(false);
    const [selectedTicket, setSelectedTicket] = useState(null);
    const [replyText, setReplyText] = useState('');
    const [isFaqModalOpen, setIsFaqModalOpen] = useState(false);

    const jobSeekerFaqs = [
        { q: "How do I update my verified resume?", a: "Go to the Career Profile section. You'll see your current resume under 'Professional Assets'. Click 'Replace' to upload a new version. Our system automatically parses your new resume to update matching algorithms." },
        { q: "How long does it take for a recruiter to respond?", a: "Recruiter response times vary by company, but on average, candidates receive feedback within 5-7 business days. You can track all status changes directly in the 'Applications Tracker' hub." },
        { q: "Who can see my profile details?", a: "Only verified recruiters from enterprise partners can view your full profile and resume. Your data is encrypted and never shared with 3rd party advertisers." },
        { q: "Can I apply for multiple jobs simultaneously?", a: "Yes! There is no limit to the number of applications you can submit. However, we recommend focusing on roles that closely match your 'Technical Proficiencies' for better success rates." },
        { q: "Why is my application locked?", a: "Applications are locked until your Career Profile reaches 100% completion. Ensure you have added technical skills, a professional bio, and your base location." },
        { q: "How do I withdraw an application?", a: "Navigate to the 'Applications Tracker' and click on the 'Withdraw' action for the specific job post. Please note this action is permanent." }
    ];
    const fetchTickets = async () => {
        try {
            const token = localStorage.getItem('token');
            const res = await axios.get('/api/support/tickets', {
                headers: { Authorization: `Bearer ${token}` }
            });
            setTickets(res.data);
            setLoading(false);
        } catch (err) {
            console.error("Error fetching tickets", err);
            setLoading(false);
        }
    };

    useEffect(() => { fetchTickets(); }, []);

    const fetchTicketDetails = async (id) => {
        try {
            const token = localStorage.getItem('token');
            const res = await axios.get(`/api/support/tickets/${id}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setSelectedTicket(res.data);
            setIsThreadModalOpen(true);
        } catch (err) {
            showError('Thread Error', 'Could not retrieve ticket conversation.');
        }
    };

    const handleReply = async (e) => {
        e.preventDefault();
        if (!replyText.trim()) return;

        showLoading('Transmitting Reply...');
        try {
            const token = localStorage.getItem('token');
            await axios.post(`/api/support/tickets/${selectedTicket._id}/reply`,
                { messageText: replyText },
                { headers: { Authorization: `Bearer ${token}` } }
            );
            setReplyText('');
            closeLoading();
            fetchTicketDetails(selectedTicket._id);
        } catch (err) {
            closeLoading();
            showError('Transmission Error', 'Failed to send your message.');
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!form.subject.trim() || !form.description.trim()) {
            showError('Incomplete Form', 'Please fill in both the subject and description.');
            return;
        }
        setSubmitting(true);
        showLoading('Submitting your support request...');
        try {
            const token = localStorage.getItem('token');
            await axios.post('/api/support/tickets', form, {
                headers: { Authorization: `Bearer ${token}` }
            });
            closeLoading();
            showSuccess('Ticket Raised', 'Our support team will respond within 24 hours.');
            setForm({ subject: '', category: 'General Query', description: '', priority: 'Normal' });
            setShowForm(false);
            fetchTickets();
        } catch (err) {
            closeLoading();
            showError('Submission Failed', 'Could not submit your ticket. Please try again.');
        } finally {
            setSubmitting(false);
        }
    };

    const filteredTickets = tickets.filter(t =>
        t.subject.toLowerCase().includes(searchTerm.toLowerCase()) ||
        t.description?.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const stats = {
        total: tickets.length,
        open: tickets.filter(t => t.status === 'Open').length,
        resolved: tickets.filter(t => t.status === 'Resolved').length,
    };

    if (loading) return <Loader />;

    const ticketColumns = [
        { header: 'Ticket ID', key: '_id', width: '20%', render: (row) => <span className="identifier-hash">#{row._id.slice(-6).toUpperCase()}</span> },
        { header: 'Subject / Area', key: 'subject', width: '35%', render: (row) => <div><div style={{ fontWeight: 800 }}>{row.subject}</div><div style={{ fontSize: '11px', color: '#64748B' }}>{row.category}</div></div> },
        {
            header: 'Criticality',
            key: 'priority',
            width: '15%',
            render: (row) => (
                <span className={`priority-tag ${row.priority?.toLowerCase()}`}>
                    {row.priority}
                </span>
            )
        },
        {
            header: 'Status',
            key: 'status',
            width: '15%',
            render: (row) => {
                const cfg = STATUS_CONFIG[row.status] || STATUS_CONFIG['Open'];
                return (
                    <span className={`ticket-status-pill ${cfg.className}`} style={{ justifyContent: 'center' }}>
                        {cfg.icon} {row.status}
                    </span>
                );
            }
        },
        {
            header: 'Engagement', key: 'actions', width: '15%', render: (row) => (
                <div className="action-buttons">
                    <button className="action-btn-gold sm" title="Open Conversation Thread" onClick={() => fetchTicketDetails(row._id)}>
                        <FaRegCommentDots />
                    </button>
                </div>
            )
        }
    ];

    return (
        <div className="student-support fade-in">
            {/* --- Page Header --- */}
            <div className="support-hero">
                <div className="support-hero-left">
                    <div className="support-hero-icon"><FaHeadset /></div>
                    <div>
                        <h1 className="page-title">Support Helpdesk</h1>
                        <p className="page-subtitle">
                            Expert assistance for every challenge. Our team is standing by.
                        </p>
                    </div>
                </div>
                <div className="support-hero-right" style={{ display: 'flex', gap: '12px' }}>
                    <button
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                            transition: 'transform 0.2s ease',
                            cursor: 'pointer',
                            background: '#fff',
                            color: '#002C5F',
                            border: '1px solid #E2E8F0',
                            padding: '10px 20px',
                            borderRadius: '12px',
                            fontWeight: 700,
                            fontSize: '14px'
                        }}
                        onMouseOver={e => e.currentTarget.style.transform = 'scale(1.05)'}
                        onMouseOut={e => e.currentTarget.style.transform = 'scale(1)'}
                        onClick={() => setIsFaqModalOpen(true)}
                    >
                        <FaQuestionCircle color="#D4AF37" /> FAQ Hub
                    </button>
                    <button className="primary-btn pulse-on-hover" onClick={() => setShowForm(!showForm)}>
                        {showForm ? <FaTimes /> : <FaPlus />}
                        {showForm ? 'Cancel' : 'New Support Ticket'}
                    </button>
                </div>
            </div>

            {/* --- Stats Strip --- */}
            <div className="support-stats-strip">
                <div className="s-stat-card">
                    <div className="s-stat-icon total"><FaTicketAlt /></div>
                    <div>
                        <h3>{stats.total}</h3>
                        <p>Total Tickets</p>
                    </div>
                </div>
                <div className="s-stat-card">
                    <div className="s-stat-icon open"><FaClock /></div>
                    <div>
                        <h3>{stats.open}</h3>
                        <p>Open Tickets</p>
                    </div>
                </div>
                <div className="s-stat-card">
                    <div className="s-stat-icon resolved"><FaCheckCircle /></div>
                    <div>
                        <h3>{stats.resolved}</h3>
                        <p>Resolved</p>
                    </div>
                </div>
                <div className="s-stat-card">
                    <div className="s-stat-icon secure"><FaShieldAlt /></div>
                    <div>
                        <h3>24h</h3>
                        <p>Response SLA</p>
                    </div>
                </div>
            </div>

            {/* --- Ticket Creation Form --- */}
            {showForm && (
                <div className="ticket-form-card">
                    <div className="form-card-header">
                        <h3><FaCommentDots /> Raise a Support Ticket</h3>
                        <p>Describe your issue clearly and our team will be on it promptly.</p>
                    </div>
                    <form onSubmit={handleSubmit} className="ticket-form">
                        <div className="form-row-support">
                            <div className="form-group-support">
                                <label>Subject</label>
                                <input
                                    className="glass-input"
                                    value={form.subject}
                                    onChange={e => setForm({ ...form, subject: e.target.value })}
                                    placeholder="e.g. Unable to access job portal"
                                    required
                                />
                            </div>
                            <div className="form-group-support">
                                <label>Interaction Sector</label>
                                <select
                                    className="glass-input"
                                    value={form.category}
                                    onChange={e => setForm({ ...form, category: e.target.value })}
                                    required
                                >
                                    <option value="Technical Issue">Platform Bug / Technical Error</option>
                                    <option value="HR / Payroll">HR, Salary, or Payroll Dispute</option>
                                    <option value="Software Reqs">Software &amp; Tool Requisitions</option>
                                    <option value="Student Reporting">Reporting Specific Behaviour</option>
                                    <option value="General Query">Administrative Enquiry</option>
                                </select>
                            </div>
                            <div className="form-group-support">
                                <label>Priority Level</label>
                                <select
                                    className="glass-input"
                                    value={form.priority}
                                    onChange={e => setForm({ ...form, priority: e.target.value })}
                                >
                                    <option value="Normal">Normal</option>
                                    <option value="High">High</option>
                                    <option value="Urgent">Urgent</option>
                                </select>
                            </div>
                        </div>
                        <div className="form-group-support">
                            <label>Description</label>
                            <textarea
                                className="glass-input"
                                rows="5"
                                value={form.description}
                                onChange={e => setForm({ ...form, description: e.target.value })}
                                placeholder="Provide as much detail as possible — steps to reproduce, error messages, screenshots context, etc."
                                required
                            />
                        </div>
                        <div className="form-actions-support">
                            <button type="button" className="cancel-form-btn" onClick={() => setShowForm(false)}>
                                <FaTimes /> Cancel
                            </button>
                            <button type="submit" className="primary-btn" disabled={submitting}>
                                <FaHeadset /> Submit Ticket
                            </button>
                        </div>
                    </form>
                </div>
            )}

            {/* --- Tickets List --- */}
            <div className="tickets-section">
                <div className="tickets-section-header">
                    <h2 className="section-label"><FaTicketAlt /> Mission Critical Support History</h2>
                </div>

                {tickets.length > 0 ? (
                    <DataTable
                        columns={ticketColumns}
                        data={filteredTickets}
                        filterOptions={[
                            { label: 'Full Ledger', value: '' },
                            { label: 'Active Issues', value: 'Open' },
                            { label: 'Work In Progress', value: 'In Progress' },
                            { label: 'Archived/Resolved', value: 'Resolved' }
                        ]}
                    />
                ) : (
                    <div className="empty-support">
                        <div className="empty-icon"><FaHeadset /></div>
                        <h3>No tickets found</h3>
                        <p>{searchTerm ? 'No tickets match your search.' : 'All clear! No active support requests.'}</p>
                        {!searchTerm && (
                            <button className="primary-btn" onClick={() => setShowForm(true)}>
                                <FaPlus /> Raise Your First Ticket
                            </button>
                        )}
                    </div>
                )}
            </div>

            {/* Conversation Thread Modal */}
            {isThreadModalOpen && selectedTicket && createPortal(
                <div className={`modal-overlay ${isThreadModalOpen ? 'open' : ''}`} onClick={() => setIsThreadModalOpen(false)} style={{ background: 'rgba(2, 6, 23, 0.85)', backdropFilter: 'blur(12px)', position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <div className="modal-content fade-in" onClick={e => e.stopPropagation()} style={{
                        maxWidth: '1000px',
                        width: '90%',
                        height: '92vh',
                        display: 'flex',
                        flexDirection: 'column',
                        background: '#fff',
                        borderRadius: '32px',
                        overflow: 'hidden',
                        boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5)',
                        border: '1px solid rgba(255, 255, 255, 0.1)'
                    }}>
                        {/* Executive Header */}
                        <div className="modal-header" style={{
                            padding: '32px 40px',
                            background: 'linear-gradient(135deg, #0F172A 0%, #1E293B 100%)',
                            color: '#fff',
                            borderBottom: 'none',
                            position: 'relative',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center'
                        }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '24px' }}>
                                <div style={{
                                    width: '64px',
                                    height: '64px',
                                    background: 'rgba(212, 175, 55, 0.15)',
                                    color: '#D4AF37',
                                    borderRadius: '18px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    fontSize: '28px',
                                    border: '1px solid rgba(212, 175, 55, 0.3)',
                                    boxShadow: '0 0 20px rgba(212, 175, 55, 0.1)'
                                }}>
                                    <FaTicketAlt />
                                </div>
                                <div>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '4px' }}>
                                        <h2 style={{ margin: 0, fontSize: '24px', fontWeight: 800, letterSpacing: '-0.5px', color: '#fff' }}>Support Command Center</h2>
                                        <span style={{
                                            background: selectedTicket.status === 'Resolved' ? '#10B981' : '#F59E0B',
                                            color: '#fff',
                                            padding: '4px 12px',
                                            borderRadius: '20px',
                                            fontSize: '11px',
                                            fontWeight: 800,
                                            textTransform: 'uppercase'
                                        }}>{selectedTicket.status}</span>
                                    </div>
                                    <p style={{ margin: 0, fontSize: '13px', color: '#94A3B8', fontWeight: 500 }}>
                                        Trace ID: <span style={{ color: '#D4AF37' }}>#{selectedTicket._id.toUpperCase()}</span> • Sector: {selectedTicket.category}
                                    </p>
                                </div>
                            </div>
                            <button
                                className="modal-close-btn"
                                onClick={() => setIsThreadModalOpen(false)}
                                style={{
                                    color: '#fff',
                                    opacity: 1,
                                    fontSize: '24px',
                                    background: 'transparent',
                                    border: 'none',
                                    cursor: 'pointer',
                                    transition: 'all 0.3s ease',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    padding: '8px'
                                }}
                            >
                                <FaTimes />
                            </button>
                        </div>

                        {/* Subject Intelligence Strip */}
                        <div style={{ padding: '16px 40px', background: '#F1F5F9', borderBottom: '1px solid #E2E8F0', display: 'flex', alignItems: 'center', gap: '12px' }}>
                            <span style={{ fontSize: '11px', fontWeight: 700, color: '#64748B', textTransform: 'uppercase', letterSpacing: '1px' }}>Active Subject:</span>
                            <span style={{ fontSize: '14px', fontWeight: 600, color: '#0F172A' }}>{selectedTicket.subject}</span>
                        </div>

                        {/* Communication Vault (Chat Area) */}
                        <div className="modal-body" style={{ flex: 1, overflowY: 'auto', background: '#F8FAFC', padding: '40px' }}>
                            <div className="chat-container" style={{ display: 'flex', flexDirection: 'column', gap: '32px' }}>
                                {selectedTicket.messages.map((msg, idx) => {
                                    const isAdmin = msg.senderModel === 'super_admin';
                                    return (
                                        <div key={idx} style={{
                                            alignSelf: isAdmin ? 'flex-start' : 'flex-end',
                                            maxWidth: '80%',
                                            display: 'flex',
                                            flexDirection: 'column',
                                            alignItems: isAdmin ? 'flex-start' : 'flex-end',
                                            animation: 'fadeInUp 0.4s ease-out forwards'
                                        }}>
                                            <div style={{
                                                background: isAdmin ? '#fff' : 'linear-gradient(135deg, #002c5f 0%, #1E3A8A 100%)',
                                                color: isAdmin ? '#1E293B' : '#fff',
                                                padding: '20px 24px',
                                                borderRadius: '24px',
                                                borderBottomLeftRadius: isAdmin ? '4px' : '24px',
                                                borderBottomRightRadius: isAdmin ? '24px' : '4px',
                                                boxShadow: isAdmin ? '0 10px 15px -3px rgba(0, 0, 0, 0.05)' : '0 10px 15px -3px rgba(30, 58, 138, 0.2)',
                                                fontSize: '15px',
                                                lineHeight: '1.7',
                                                border: isAdmin ? '1px solid #E2E8F0' : 'none',
                                                position: 'relative'
                                            }}>
                                                {msg.messageText}
                                            </div>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '10px' }}>
                                                <span style={{
                                                    fontSize: '11px',
                                                    color: isAdmin ? '#002c5f' : '#64748B',
                                                    fontWeight: 800,
                                                    textTransform: 'uppercase',
                                                    letterSpacing: '0.5px'
                                                }}>
                                                    {isAdmin ? 'ADMINISTRATIVE COMMAND' : 'FIELD OPERATIONS (YOU)'}
                                                </span>
                                                <span style={{ fontSize: '11px', color: '#CBD5E1' }}>•</span>
                                                <span style={{ fontSize: '11px', color: '#94A3B8', fontWeight: 500 }}>
                                                    {new Date(msg.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                                </span>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>

                        {/* Command Input Area */}
                        {selectedTicket.status !== 'Resolved' ? (
                            <div className="modal-actions-chat" style={{ padding: '20px 40px 28px', background: '#fff', borderTop: '1px solid #E2E8F0', boxShadow: '0 -10px 20px rgba(0,0,0,0.02)', marginTop: 0 }}>
                                <form onSubmit={handleReply} style={{ width: '100%', display: 'flex', gap: '20px', alignItems: 'center' }}>
                                    <div style={{ flex: 1, position: 'relative' }}>
                                        <input
                                            type="text"
                                            className="glass-input"
                                            placeholder="Transmit technical response to Command Center..."
                                            value={replyText}
                                            onChange={(e) => setReplyText(e.target.value)}
                                            style={{
                                                height: '64px',
                                                borderRadius: '20px',
                                                padding: '0 30px',
                                                fontSize: '16px',
                                                background: '#F1F5F9',
                                                border: '2px solid transparent',
                                                transition: 'all 0.3s ease',
                                                width: '100%'
                                            }}
                                        />
                                    </div>
                                    <button type="submit" className="primary-btn pulse-on-hover" style={{
                                        width: '72px',
                                        height: '64px',
                                        borderRadius: '20px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        fontSize: '24px',
                                        boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)',
                                        border: 'none',
                                        background: '#0FB14E',
                                        color: '#fff',
                                        cursor: 'pointer'
                                    }}>
                                        <FaPaperPlane />
                                    </button>
                                </form>
                            </div>
                        ) : (
                            <div style={{
                                padding: '32px',
                                textAlign: 'center',
                                background: 'linear-gradient(to right, #F1F5F9, #F8FAFC, #F1F5F9)',
                                color: '#475569',
                                fontSize: '15px',
                                fontWeight: 600,
                                borderTop: '1px solid #E2E8F0',
                                letterSpacing: '0.5px'
                            }}>
                                <FaCheckCircle style={{ marginRight: '10px', color: '#10B981', fontSize: '18px' }} />
                                Operational Mission Finalized. Communication channel archived.
                            </div>
                        )}
                    </div>
                </div>,
                document.body
            )}
            {/* FAQ HUB MODAL - Job Seeker Edition */}
            {isFaqModalOpen && createPortal(
                <div className={`modal-overlay ${isFaqModalOpen ? 'open' : ''}`} onClick={() => setIsFaqModalOpen(false)} style={{ background: 'rgba(2, 6, 23, 0.85)', backdropFilter: 'blur(12px)', position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <div className="modal-content glass-card fade-in" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '800px', background: '#fff', borderRadius: '24px', overflow: 'hidden' }}>
                        <div className="modal-header" style={{
                            padding: '24px 32px',
                            background: 'linear-gradient(135deg, #0F172A 0%, #1E293B 100%)',
                            color: '#fff',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center'
                        }}>
                            <h2 style={{
                                margin: 0,
                                fontSize: '20px',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '16px',
                                color: '#ffffff'
                            }}>
                                <FaQuestionCircle color="#D4AF37" /> Career Support Intelligence Hub
                            </h2>
                            <button
                                className="modal-close-btn"
                                onClick={() => setIsFaqModalOpen(false)}
                                style={{
                                    color: '#fff',
                                    background: 'rgba(255,255,255,0.1)',
                                    border: 'none',
                                    fontSize: '20px',
                                    cursor: 'pointer',
                                    marginLeft: '24px'
                                }}
                            >
                                <FaTimes />
                            </button>
                        </div>
                        <div className="modal-body" style={{ padding: '32px' }}>
                            <div className="faq-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
                                {jobSeekerFaqs.map((item, i) => (
                                    <div key={i} style={{ background: '#F8FAFC', padding: '20px', borderRadius: '16px', border: '1px solid #E2E8F0', transition: 'transform 0.2s ease' }}>
                                        <div style={{ fontWeight: 800, color: '#0F172A', marginBottom: '10px', display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14px' }}>
                                            <FaBolt color="#D4AF37" /> {item.q}
                                        </div>
                                        <p style={{ margin: 0, fontSize: '13px', color: '#64748B', lineHeight: '1.6', fontWeight: 500 }}>{item.a}</p>
                                    </div>
                                ))}
                            </div>
                            <div style={{ marginTop: '32px', padding: '24px', background: 'rgba(0, 44, 95, 0.05)', borderRadius: '20px', textAlign: 'center', border: '1px solid rgba(0, 44, 95, 0.1)' }}>
                                <p style={{ margin: 0, color: '#002C5F', fontWeight: 700, fontSize: '15px' }}>Still need specialized operational assistance?</p>
                                <button
                                    className="primary-btn sm"
                                    onClick={() => {
                                        setIsFaqModalOpen(false);
                                        setShowForm(true);
                                    }}
                                    style={{ marginTop: '16px', width: '100%', justifyContent: 'center', background: '#002C5F' }}
                                >
                                    Transmit Ticket to Helpdesk <FaChevronDown style={{ marginLeft: '8px', transform: 'rotate(-90deg)', fontSize: '12px' }} />
                                </button>
                            </div>
                        </div>
                    </div>
                </div>,
                document.body
            )}
        </div>
    );
};

export default JobSeekerSupport;
